import { Box, Stack, Typography, Button, IconButton, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';

const MAX_TIMEOFF_INSTANCES_PER_DAY = 10;

const TimeoffSection = ({
    timeoffInstances,
    myTimeoffHours,
    partnerTimeoffHours,
    relationshipName,
    onAddClick,
    onEdit,
    onDelete,
    onRemoveComment,
}) => {
    const partnerFirstName = relationshipName?.split(' ')[0] || 'Partner';
    const maxReached = timeoffInstances.length >= MAX_TIMEOFF_INSTANCES_PER_DAY;

    return (
        <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ display: 'inline-block' }}>
                        Time Off
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: 'rgba(123, 107, 141, 0.12)', border: '1px solid #7B6B8D' }}>
                            <Typography variant="caption" sx={{ color: '#7B6B8D', fontWeight: 600 }}>
                                Me: {myTimeoffHours}h
                            </Typography>
                        </Box>
                        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, backgroundColor: 'rgba(196, 133, 106, 0.12)', border: '1px solid #C4856A' }}>
                            <Typography variant="caption" sx={{ color: '#C4856A', fontWeight: 600 }}>
                                {partnerFirstName}: {partnerTimeoffHours}h
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <Tooltip title={maxReached ? `Maximum of ${MAX_TIMEOFF_INSTANCES_PER_DAY} time-off entries per day reached` : ''}>
                    <span>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onAddClick}
                            disabled={maxReached}
                            sx={{
                                borderRadius: 2, background: '#7B6B8D', '&:hover': { background: '#6A5A7A' },
                                '&.Mui-disabled': { background: '#E8E8E8', color: '#999' }
                            }}
                        >
                            Add Time Off
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {timeoffInstances.length > 0 ? (
                timeoffInstances.map((time) => (
                    <Box
                        key={time.timeoff_instance_id}
                        sx={{
                            p: 2, mt: 2, borderRadius: 2, border: '1px solid',
                            borderColor: time.value === '0' ? '#7B6B8D' : '#C4856A',
                            backgroundColor: time.value === '0' ? 'rgba(184, 167, 212, 0.04)' : 'rgba(244, 181, 196, 0.04)',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                                        {time.name}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Box sx={{
                                            px: 1.5, py: 0.5, borderRadius: 1,
                                            background: time.value === '0' ? '#7B6B8D' : '#C4856A',
                                            color: 'white', fontSize: '0.75rem', fontWeight: 600
                                        }}>
                                            {time.value === '0' ? 'Me' : partnerFirstName}
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#666' }}>
                                            {time.hours} {time.hours === 1 ? 'hour' : 'hours'}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Edit time off">
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit(time)}
                                            sx={{ color: '#7B6B8D', '&:hover': { backgroundColor: 'rgba(184, 167, 212, 0.1)' } }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete time off">
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete(time.timeoff_instance_id)}
                                            sx={{ color: '#C4856A', '&:hover': { backgroundColor: 'rgba(244, 181, 196, 0.1)' } }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                            {time.comments && (
                                <Box sx={{
                                    px: 1.5, py: 0.75, borderRadius: 1.5,
                                    backgroundColor: time.value === '0' ? 'rgba(184, 167, 212, 0.08)' : 'rgba(244, 181, 196, 0.08)',
                                    display: 'flex', alignItems: 'center', gap: 1,
                                }}>
                                    <Typography variant="body2" sx={{ color: '#555', flex: 1, fontSize: '0.8rem' }}>
                                        {time.comments}
                                    </Typography>
                                    <Tooltip title="Remove comment">
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemoveComment('timeoff', time.timeoff_instance_id)}
                                            sx={{ color: '#bbb', p: 0.25, flexShrink: 0, '&:hover': { color: '#C4856A', backgroundColor: 'transparent' } }}
                                        >
                                            <ClearIcon sx={{ fontSize: '0.85rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                ))
            ) : (
                <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 4 }}>
                    No time off instances added.
                </Typography>
            )}
        </Box>
    );
};

export default TimeoffSection;
